import {
  countryName,
  Forbidden,
  formatWhole,
  formatRange,
  getCountryFacts,
  getDocs,
  getPlan,
  NotFound,
  Popover,
  type CountriesFacts,
  type DocsScreen,
  type MoneyRange,
  type Plan,
  type PlanStop,
} from '@trippath/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { CountryRow, NightsList } from './CountryRow';
import { CrossingTask, isForm, isVisa } from './CrossingTask';
import { countryViews, dayRange, type CountryView } from '../../lib/countryFacts';
import { dayShort } from '../../lib/format';
import { StayBookings } from '../../components/booking/StayBookings';
import { useLive } from '../../app/live';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan };

/** What the ways between countries cost: each leg on whose next stop is in another country. */
function between(plan: Plan): { range: MoneyRange; count: number } | null {
  const legs = plan.stops.flatMap((stop, index) => {
    const next = plan.stops[index + 1];
    return stop.out && next && next.country !== stop.country ? [stop.out.estimate] : [];
  });
  const first = legs[0];
  if (!first) return null;
  const range = legs.slice(1).reduce(
    (sum, leg) => ({
      min: { ...sum.min, minor: sum.min.minor + leg.min.minor },
      max: { ...sum.max, minor: sum.max.minor + leg.max.minor },
    }),
    first,
  );
  return { range, count: legs.length };
}

/** The four figures over the countries: how many, entry to get, forms to fill, nights booked. */
/** A tile's figure, pressed to show what it counts beside it: which borders, which nights. */
function TileFigure({
  className,
  said,
  title,
  children,
}: {
  className: string;
  said: string;
  title: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label={title}
      trigger={(props) => (
        <p className={className}>
          <button
            type="button"
            className="countries-tile-button"
            aria-expanded={open}
            {...props}
          >
            {said}
          </button>
        </p>
      )}
    >
      <div className="countries-tile-pop">
        {/* Named by the tile it opens from: its heading not said again. */}
        {children}
      </div>
    </Popover>
  );
}

function Tiles({
  plan,
  views,
  docs,
  onStop,
}: {
  plan: Plan;
  views: CountryView[];
  docs: DocsScreen | null;
  onStop: (stop: PlanStop) => void;
}) {
  const { t, i18n } = useTranslation();
  // The borders still to see to, by what they ask; one let in by a permit got before asks nothing.
  const due = crossingRows(views).filter(
    ({ crossing }) => !crossing.done && crossing.due !== null,
  );
  const lang = i18n.language;
  const crossings = docs ? docs.crossings.filter((c) => !c.done && c.due !== null) : [];
  const visas = crossings.filter(isVisa);
  const forms = crossings.filter(isForm);
  const nights = views.reduce((all, v) => all + v.nights, 0);
  const booked = views.reduce((all, v) => all + v.booked, 0);
  const cities = plan.stops.filter((s) => !s.transfer).length;
  const [visa] = visas;
  const [form] = forms;
  const noPassport = docs?.passport === null;
  return (
    <div className="countries-tiles">
      <section className="panel countries-tile">
        <h2>{t('countries.tiles.countries')}</h2>
        <p className="countries-tile-figure mono">
          {views.filter((view) => view.stops.length > 0).length}
        </p>
        <p className="quiet">
          {t('countries.tiles.countriesSub', { days: plan.totals.days, cities })}
        </p>
      </section>
      <section className="panel countries-tile">
        <h2>{t('countries.tiles.entry')}</h2>
        {noPassport ? (
          <>
            <p className="countries-tile-figure">
              <Link to={`/trips/${plan.trip.id}/docs`}>{t('countries.tiles.noPassport')}</Link>
            </p>
            <p className="quiet">{t('countries.tiles.noPassportSub')}</p>
          </>
        ) : (
          <>
            {visa ? (
              <TileFigure
                className="countries-tile-figure countries-alert"
                said={t('countries.tiles.visas', { count: visas.length })}
                title={t('countries.tiles.entry')}
              >
                <CrossingList rows={due.filter(({ crossing }) => isVisa(crossing))} />
              </TileFigure>
            ) : (
              <p className="countries-tile-figure">{t('countries.tiles.noVisas')}</p>
            )}
            {visa?.rule && visa.due !== null && (
              <p className="quiet">
                {t('countries.tiles.visaNext', {
                  country: countryName(visa.country, lang),
                  kind: t(`countries.rule.${visa.rule.kind}`),
                  day: dayShort(visa.due, lang),
                })}
              </p>
            )}
          </>
        )}
      </section>
      <section className="panel countries-tile">
        <h2>{t('countries.tiles.forms')}</h2>
        {form ? (
          <TileFigure
            className="countries-tile-figure countries-warn"
            said={t('countries.tiles.formsToFill', { count: forms.length })}
            title={t('countries.tiles.forms')}
          >
            <CrossingList rows={due.filter(({ crossing }) => isForm(crossing))} />
          </TileFigure>
        ) : (
          <p className="countries-tile-figure">{t('countries.tiles.noForms')}</p>
        )}
        {form?.rule && form.due !== null && (
          <p className="quiet">
            {t('countries.tiles.formNext', {
              form: form.rule.form,
              range: dayRange(form.due, form.border, lang),
            })}
          </p>
        )}
      </section>
      <section className="panel countries-tile">
        <h2>{t('countries.tiles.nights')}</h2>
        {/* Pressed, where a bed is booked and where not, stop by stop in order. */}
        <TileFigure
          className="countries-tile-figure mono"
          said={`${String(booked)} / ${String(nights)}`}
          title={t('countries.tiles.nights')}
        >
          <NightsList stops={views.flatMap((view) => view.stops)} onStop={onStop} />
        </TileFigure>
        <span className="countries-meter" aria-hidden="true">
          <span style={{ width: `${String(nights > 0 ? (booked / nights) * 100 : 0)}%` }} />
        </span>
      </section>
    </div>
  );
}

/** The borders in the order they are crossed, each with what it asks and by when. */
/** Each border crossed, by when, with where it is entered: the first crossing of a country by
 * its airport or town. */
function crossingRows(views: CountryView[]) {
  const rows = views.flatMap((view) =>
    view.crossings.map((crossing, index) => ({
      crossing,
      at: index === 0 ? view.enters : null,
    })),
  );
  return rows.sort((a, b) => a.crossing.border.localeCompare(b.crossing.border));
}

/** Borders in order: the day, the country entered, what it asks and by when. */
function CrossingList({ rows }: { rows: ReturnType<typeof crossingRows> }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  return (
    <ol className="countries-crossing-list">
      {rows.map(({ crossing, at }) => {
        const rule = crossing.rule;
        return (
          <li key={`${crossing.country}-${String(crossing.entry)}`}>
            <span className="mono quiet">{dayShort(crossing.border, lang)}</span>
            <div>
              <strong>
                {t('countries.arrive', { country: countryName(crossing.country, lang) })}
                {at !== null && ` · ${at}`}
              </strong>
              <p className="quiet">
                {rule
                  ? t(`countries.need.${rule.kind}`, { form: rule.form, lead: rule.lead })
                  : t('countries.unknownRule')}
              </p>
              <CrossingTask crossing={crossing} />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Crossings({ views, docs }: { views: CountryView[]; docs: DocsScreen }) {
  const { t } = useTranslation();
  return (
    <section className="countries-crossings" aria-labelledby="crossings-title">
      <h2 id="crossings-title">{t('countries.crossings')}</h2>
      <div className="panel">
        <CrossingList rows={crossingRows(views)} />
      </div>
      <p className="quiet countries-note">
        <PassportNote docs={docs} views={views} /> {t('countries.indicative')}
      </p>
    </section>
  );
}

/** The passport travelled on and whether it is valid six months past the trip's end. */
function PassportNote({ docs, views }: { docs: DocsScreen; views: CountryView[] }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  if (docs.passport === null) return null;
  const country = countryName(docs.passport, lang);
  const document = docs.documents.find((d) => d.kind === 'passport' && d.mine);
  const validTo = document?.validTo ?? null;
  if (validTo === null) return <>{t('countries.passportNone', { country })}.</>;
  const back = views.reduce((last, v) => (v.last > last ? v.last : last), docs.end);
  const needed = new Date(`${back}T00:00:00Z`);
  needed.setUTCMonth(needed.getUTCMonth() + 6);
  const long = validTo >= needed.toISOString().slice(0, 10);
  return (
    <>
      {t('countries.passportValid', { country, day: dayShort(validTo, lang) })}{' '}
      <span className={long ? '' : 'countries-alert'}>
        {t(long ? 'countries.passportLong' : 'countries.passportShort')}
      </span>
      .
    </>
  );
}

/** Countries (the Countries mockup): each country's days, money, entry, nights and what to know. */
export function Countries() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const { id = '' } = useParams();
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [docs, setDocs] = useState<DocsScreen | null>(null);
  const [facts, setFacts] = useState<CountriesFacts | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  // The stop whose beds are open in a dialog.
  const [stayOf, setStayOf] = useState<string | null>(null);

  useEffect(() => {
    getPlan(id).then(
      (plan) => {
        setState({ kind: 'ready', plan });
      },
      (error: unknown) => {
        if (error instanceof Forbidden) void navigate(`/trips/${id}/view`, { replace: true });
        else setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    // Entry, money and holidays are said when they come; the page stands without them.
    getDocs(id).then(setDocs, () => undefined);
  }, [id, navigate, changed]);

  useEffect(() => {
    getCountryFacts(id, lang).then(setFacts, () => undefined);
  }, [id, lang]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p role="alert">{t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}</p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  const { plan } = state;
  const views = countryViews(plan, docs, facts ? facts.countries : []);
  const rows = plan.countries.map((row) => ({
    row,
    min: row.days > 0 ? row.total.min.minor / row.days : 0,
    max: row.days > 0 ? row.total.max.minor / row.days : 0,
  }));
  const scale = Math.max(1, ...rows.map((r) => r.max));
  const ways = between(plan);
  const crossings = docs ? docs.crossings.length : 0;

  const stayStop = plan.stops.find((stop) => stop.id === stayOf);
  return (
    <div className="countries-page">
      <div className="countries-head">
        <h1 className="visually-hidden">{t('countries.title')}</h1>
        <p className="countries-span">
          {t('countries.span', {
            range: dayRange(plan.trip.start, plan.trip.end, lang),
            days: plan.totals.days,
          })}
        </p>
        <p className="countries-expected">
          {t('countries.expected')}{' '}
          <span className="mono">{formatWhole(plan.totals.expected, lang)}</span>{' '}
          {t('countries.of', { budget: formatWhole(plan.trip.budget, lang) })}
        </p>
        <Link
          className="link-button countries-footprint"
          to={`/trips/${plan.trip.id}/emissions`}
        >
          {t('emissions.open')}
        </Link>
        <Link className="link-button countries-footprint" to={`/trips/${plan.trip.id}/actual`}>
          {t('actual.open')}
        </Link>
      </div>
      <div className="countries-body">
        <Tiles
          plan={plan}
          views={views}
          docs={docs}
          onStop={(stop) => {
            setStayOf(stop.id);
          }}
        />
        <div className="countries-columns">
          <section className="panel countries-table" aria-labelledby="countries-title">
            <div className="countries-table-head">
              {/* The column says Country: the heading is for screen readers. */}
              <h2 id="countries-title" className="visually-hidden">
                {t('countries.byCountry')}
              </h2>
              <p className="quiet day-legend">
                <span className="day-legend-dot" aria-hidden="true" />{' '}
                {t('countries.legendExpected')}
                <span className="day-legend-range" aria-hidden="true" />{' '}
                {t('countries.legendRange')}
                <span className="day-legend-dot day-legend-dear" aria-hidden="true" />{' '}
                {t('countries.legendDear', { over: formatWhole(plan.totals.dearPerDay, lang) })}
              </p>
            </div>
            {rows.length === 0 ? (
              <p className="quiet">{t('plan.noStops')}</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th scope="col">{t('plan.country')}</th>
                    <th scope="col">{t('countries.nights')}</th>
                    <th scope="col">{t('countries.aDay')}</th>
                    <th scope="col">{t('plan.total')}</th>
                    <th scope="col">{t('countries.entryBooked')}</th>
                  </tr>
                </thead>
                {rows.flatMap(({ row, min, max }) =>
                  views
                    .filter((view) => view.country === row.country)
                    .map((view) => (
                      <CountryRow
                        key={row.country}
                        plan={plan}
                        row={row}
                        view={view}
                        passport={docs ? docs.passport : null}
                        band={{ min: min / scale, max: max / scale }}
                        scale={scale}
                        open={open === row.country}
                        onToggle={() => {
                          setOpen(open === row.country ? null : row.country);
                        }}
                        onStop={(stop) => {
                          setStayOf(stop.id);
                        }}
                      />
                    )),
                )}
              </table>
            )}
            {ways && (
              <p className="countries-ways">
                <span>
                  {t('countries.between')} · {ways.count}
                </span>
                <span className="mono">{formatRange(ways.range, lang, true)}</span>
              </p>
            )}
          </section>
          {docs && crossings > 0 && <Crossings views={views} docs={docs} />}
        </div>
      </div>
      {/* A stop's beds, to book, change or remove: the page's figures follow. */}
      {stayStop && (
        <StayBookings
          plan={plan}
          stop={stayStop}
          onClose={() => {
            setStayOf(null);
          }}
          onSaved={(next) => {
            setState({ kind: 'ready', plan: next });
          }}
        />
      )}
    </div>
  );
}
